import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormControl, Validators } from '@angular/forms';
import { toNumber } from 'lodash';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { debounceTime, switchMap, map, take } from 'rxjs/operators';
import { MultiInvoiceApiService } from 'src/api/services/multi-invoice-api.service';
import { CalsiumListConfig, ColumnDataType, CalsiumListColumnItem } from 'src/app/calsiumlist/calsiumlist.component.model';
import { ErrorHandlerService } from 'src/app/errorhandler/error-handler.service';
import { ErrorTypeEnum, TextAlignment } from 'src/app/shared/enums.model';
import { CalsiumDateFormatter } from 'src/app/shared/calsium-date-formatter';
import { MasterDataStateService, Paging, ScaniaUnit } from 'src/state';
import { CommonStateService } from 'src/state/common/service';
import { MultiInvoiceStateService } from 'src/state/multi-invoice/service';
import { MultiInvoice } from 'src/state/multi-invoice/state-models/multi-invoice';
import { Router } from '@angular/router';
import { ShareDataService } from 'src/app/share-data.service';
import { environment } from 'src/environments/environment';
import { LoadingItem } from 'src/state/common/state-models/loading-item';
import { ConfirmationDialogService } from 'src/app/common/confirmation-dialog/confirmation-dialog.service';

@Component({
  selector: 'cal-multi-invoice',
  templateUrl: './multi-invoice.component.html',
  styleUrl: './multi-invoice.component.css'
})

export class MultiInvoiceComponent implements OnInit, OnDestroy {

  selectedScaniaUnitId: number = 0;
  resources: any = this.commonService.getResources();
  searchComponentsForm: UntypedFormGroup;
  scaniaUnitList: Observable<ScaniaUnit[]>;
  fileName: string = '';
  showData: boolean = false;
  endDateMax: string;

  dataList: Observable<MultiInvoice[]>;
  listConfig: CalsiumListConfig;
  paging: Observable<Paging>;
  totalCount: Observable<number>;
  subscriptions = new Subscription();
  isLoading: Observable<boolean>;
  sortingExpression: Observable<string>;
  filterExpression: Observable<string>;
  selectedCount: Observable<number> = of(0);
  selectedCountValue: number = 0;
  selectAll: boolean = false;
  selectedItems: MultiInvoice[] = [];
  excludedItems: MultiInvoice[] = [];
  selectorColumn: CalsiumListColumnItem;

  constructor(
    private multiInvoiceApiService: MultiInvoiceApiService,
    private commonService: CommonStateService,
    private masterDataService: MasterDataStateService,
    private errHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    private multiInvoiceService: MultiInvoiceStateService,
    private router: Router,
    private shareData: ShareDataService,
    private confirmationDialog: ConfirmationDialogService
  ) { }

  ngOnInit() {
    this.buildForm();
    this.fillSearchForm();
    this.scaniaUnitList = this.masterDataService.getScaniaUnitsByTypes(['Final Assembly', 'Allocation']);
    this.isLoading = this.commonService.getLoading();

    this.sortingExpression = this.multiInvoiceService.getSortingExpression();
    this.filterExpression = this.multiInvoiceService.getFilterExpression();
    this.totalCount = this.multiInvoiceService.getTotalCount();
    this.paging = this.multiInvoiceService.getPaging();
    this.dataList = this.multiInvoiceService.getList();

    let pageInitialLoad = true;
    this.subscriptions.add(
      combineLatest(this.paging, this.sortingExpression, this.filterExpression).pipe(
        debounceTime(0),
        switchMap(([paging, sortingExpression, filterExpression]) => {
          if (!pageInitialLoad) {
            return this.multiInvoiceService.loadMultiInvoice(
              this.searchComponentsForm.value.selectedScaniaUnit,
              this.searchComponentsForm.value.startDate,
              this.searchComponentsForm.value.endDate,
              paging,
              sortingExpression,
              filterExpression
            );
          }
          pageInitialLoad = false;
          return new Promise<void>(null);
        })
      ).subscribe()
    );
    this.buildList();
  }

  ngOnDestroy() { this.subscriptions.unsubscribe(); }

  buildForm() {
    this.searchComponentsForm = this.formBuilder.group({
      selectedScaniaUnit: ["", Validators.required]
    });
  }

  fillSearchForm(fromDate: Date = null, toDate: Date = null, scaniaUnit: string = '') {

    this.searchComponentsForm = this.formBuilder.group({
      startDate: [CalsiumDateFormatter.getDateOrDefault(fromDate, true), []],
      endDate: [CalsiumDateFormatter.getDateOrDefault(toDate, false), []],
      selectedScaniaUnit: [scaniaUnit, <any>Validators.required]
    });

  }

  onScaniaUnitSelected(scaniaUnitId: any) {
    this.selectedScaniaUnitId = scaniaUnitId;
  }

  onExportToExcel() {
    if (!this.isValidForm()) return;
    const fromDate = this.searchComponentsForm.value.startDate;
    const toDate = this.searchComponentsForm.value.endDate;
    const scaniaUnitId = this.searchComponentsForm.value.selectedScaniaUnit;
    if (scaniaUnitId === '0') {
      this.fileName = `All_${fromDate}_${toDate}_multiInvoice`;
    }
    else {
      const scaniaUnit = this.masterDataService.getState().scaniaUnits.find(su => su.id === parseInt(scaniaUnitId));
      this.fileName = `${scaniaUnit.name}_${fromDate}_${toDate}_multiInvoice`;
    }

    const payload = {
      from: new Date(fromDate).toISOString(),
      to: new Date(toDate).toISOString(),
      allocationScaniaUnit: toNumber(scaniaUnitId),
    };
    return this.multiInvoiceApiService.exportMultiInvoice(payload, this.fileName);
  }

  buildList() {
    this.listConfig = new CalsiumListConfig();
    this.listConfig.ListCaption = this.resources.List;
    this.listConfig.ShowFilterRow = true;
    this.listConfig.ParentComponent = this;
    this.listConfig.ShowExportToExcel = false;
    this.listConfig.IsSelectorEnabled = (rowData: MultiInvoice) => { return rowData.totalInvoiceReceived < 2 };
    this.listConfig.IsSelected = this.isSelected;
    this.listConfig.ShowCheckBox = (rowData: MultiInvoice): boolean => {
      const isSlaUnit = environment.scaniaUnitIdsForSLA.includes(this.selectedScaniaUnitId);
      const count = rowData.totalInvoiceReceived < 2;
      return isSlaUnit && count;
    };
    this.listConfig.ShowSelectAllCheckBox = () => {
      return environment.scaniaUnitIdsForSLA.includes(this.selectedScaniaUnitId);
    }
    let column;
    this.selectorColumn = this.listConfig.AddListColumn(ColumnDataType.selector, null, null);
    this.selectorColumn.TextAlignment = TextAlignment.Center;
    column = this.listConfig.AddListColumn(ColumnDataType.string, 'chassisNumber', this.resources.ChassisNumber);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'productIndividualNumber', this.resources.ProductIndividualNumber);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'country', this.resources.Country);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.boolean, 'arReceive', this.resources.ARReceive);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.boolean, 'manualReceive', this.resources.ManualReceive);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.number, 'totalInvoiceReceived', this.resources.TotalInvoiceReceived);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'faDescription', this.resources.Description);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.number, 'finalAssembly', this.resources.FinalAssembly);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.date, 'invoiceDate', this.resources.InvoiceDate);
    column.AllowFilter = true;
    column.Sortable = true;

  }

  onPaging(page: number) {
    this.multiInvoiceService.goToPage(page);
  }

  onSort(sortExpression: string) {
    this.multiInvoiceService.sort(sortExpression);
  }

  onFilter(filterExpression: string) {
    this.multiInvoiceService.filter(filterExpression);
  }

  onClearFilterAndSorting() {
    this.multiInvoiceService.clearFilterAndSorting();
  }

  isSelected(data: MultiInvoice): boolean {
    const selected = this.selectAll || this.selectedItems.some(item => item.chassisNumber == data.chassisNumber);
    return selected;
  }

  showSelectActionButton() {
    // Hiding Select Action Button, if FA product is SLA and its status is Allocated
    return environment.scaniaUnitIdsForSLA.includes(this.selectedScaniaUnitId);
  }

  onSelectAllChange(select: boolean) {

    this.selectAll = select;

    this.selectedCount = select ? this.totalCount : of(0);
    this.selectedItems = [];
    this.excludedItems = [];
    this.subscriptions.add(this.selectedCount.subscribe(count => this.selectedCountValue = count));

  }

  onRowSelectionChange({ selected, rowdata }: { selected: boolean, rowdata: MultiInvoice }) {
    if (rowdata.totalInvoiceReceived >= 2) return;
    if (this.selectAll) {
      this.excludedItems = selected ? this.excludedItems.filter(
        x => x.chassisNumber !== rowdata.chassisNumber) : [...this.excludedItems, rowdata];

      this.selectedCount = combineLatest([this.totalCount]).pipe(
        map(([total]) => total - this.excludedItems.length)
      );
    }
    else {
      this.selectedItems = selected ? [...this.selectedItems, rowdata] : this.selectedItems.filter(x => x.chassisNumber !== rowdata.chassisNumber);

      this.selectedCount = of(this.selectedItems.length);
    }
    this.subscriptions.add(
      this.selectedCount.subscribe(count => this.selectedCountValue = count)
    );

  }

  exportToCsv(items: MultiInvoice[]): void {
    if (!items || items.length == 0) {
      this.errHandler.emitError(
        this.resources.RequiredRow,
        this.resources.RequiredFieldError,
        ErrorTypeEnum.Error
      );
      return;
    }
    const csvLines = items
      .map(item => `${item.chassisNumber},${item.country},`)
      .join('\n');

    this.shareData.setData(csvLines);
    this.router.navigate(['/admin/manual-invoicing']);
  }

  validate() {
    if (!this.selectAll) {
      const finalItems = this.selectedItems.filter(item => item.totalInvoiceReceived < 2);
      if (finalItems.length == 0) {
        this.errHandler.emitError(
          this.resources.RequiredRow,
          this.resources.RequiredFieldError,
          ErrorTypeEnum.Error
        );
        return;
      }
      this.confirmationDialog.show(
        this.resources.ManualInvoiceConfirmation.formatUnicorn(
          finalItems.length
        ),
        this.resources.ManualInvoice).then(
          result => {
            if (result) {
              this.exportToCsv(finalItems);
            }
          });
      return;
    }
    const loadingItem: LoadingItem = {
      key: 'validate-manual-invoice' + Date.now()
    };
    this.commonService.startLoading(loadingItem);

    combineLatest([
      this.totalCount.pipe(take(1)),
      this.sortingExpression.pipe(take(1)),
      this.filterExpression.pipe(take(1))
    ]).subscribe(([totalCount, order, baseFilter]) => {
      const combinedFilter = baseFilter ? `${baseFilter}&totalInvoiceReceived=1` : 'totalInvoiceReceived=1';

      const payload = {
        from: new Date(this.searchComponentsForm.value.startDate).toISOString(),
        to: new Date(this.searchComponentsForm.value.endDate).toISOString(),
        allocationScaniaUnit: Number(this.searchComponentsForm.value.selectedScaniaUnit),
        paging: {
          page: 1,
          offset: totalCount
        },
        order: order,
        filter: combinedFilter
      };

      this.multiInvoiceApiService.getMultiInvoiceList(payload, false).then(
        (result) => {

          const list = result.list || [];
          const finalItems = list.filter(item =>
            item.totalInvoiceReceived < 2);

          if (finalItems.length == 0) {
            this.errHandler.emitError(
              this.resources.RequiredRow,
              this.resources.RequiredFieldError,
              ErrorTypeEnum.Error
            );
            return;
          }

          this.confirmationDialog.show(
            this.resources.ManualInvoiceConfirmation.formatUnicorn(
              finalItems.length
            ),
            this.resources.ManualInvoice)
            .then(result => {
              if (result) {
                this.exportToCsv(finalItems);
              }
            });
        }).catch((error) => {
          console.error("Error fetching all pages:", error);
        }).finally(() => {
          this.commonService.finishLoading(loadingItem);
        });
    });
  }

  onSearch(): any {
    if (this.isValidForm()) {
      this.showData = true;
      const fromDate = this.searchComponentsForm.value.startDate;
      const toDate = this.searchComponentsForm.value.endDate;
      const scaniaUnitId = this.searchComponentsForm.value.selectedScaniaUnit;
      this.selectedScaniaUnitId = Number(this.searchComponentsForm.value.selectedScaniaUnit);

      return this.multiInvoiceService.refetchList(
        scaniaUnitId,
        fromDate,
        toDate);
    }
  }


  isValidForm(): boolean {
    let valid = true;
    const selectedUnit = this.searchComponentsForm.get('selectedScaniaUnit');

    if (selectedUnit?.errors?.required) {
      this.errHandler.emitError(
        this.resources.ScaniaUnitIsRequired,
        this.resources.RequiredFieldError,
        ErrorTypeEnum.Error
      );
      valid = false;
    }
    return valid;
  }

  reset(): void {
    this.searchComponentsForm.reset();
  }


}
